import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EmpleadoRepository } from 'src/app/domain/service/empleado/empleado-repository';
import {
  EmpleadoAdminResponse,
  EmpleadoDetalle,
  EmpleadoFiltro,
  EmpleadoRequest,
  EmpleadoSearchResult,
  EmpleadoUenFiltro,
} from 'src/app/domain/model/empleado/empleado';

/**
 * Adaptador HTTP: HttpEmpleadoRepository
 *
 * Implementa búsqueda, detalle, creación y modificación de empleados
 * (asesores) contra el backend — HU1259115 "Administrar Empleados/Asesores".
 * Endpoints: GET /buscar, GET /buscar-uen, GET /{id}, POST, PUT /{id}.
 *
 * Legacy: SearchersHandler.searchAdvisors() → TPSC_EMPLEADOS;
 * AdminEmpleadosActivity → CrearEmpleadoViewImpl / ModificarEmpleadoViewImpl.
 */
@Injectable({ providedIn: 'root' })
export class HttpEmpleadoRepository implements EmpleadoRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/empleados`;
  private static readonly PAGE_SIZE_MIN = 1;
  private static readonly PAGE_SIZE_MAX = 100;

  async buscar(filtro: EmpleadoFiltro, size: number, cursor?: string, direction?: string): Promise<EmpleadoSearchResult> {
    this.validarPaginacion(size);
    const params = this.buildFiltroParams(filtro, size, cursor, direction);

    return firstValueFrom(
      this.http.get<EmpleadoSearchResult>(`${this.baseUrl}/buscar`, { params })
    );
  }

  async buscarPorUen(filtro: EmpleadoUenFiltro, size: number, cursor?: string, direction?: string): Promise<EmpleadoSearchResult> {
    this.validarPaginacion(size);
    let params = this.buildFiltroParams(filtro, size, cursor, direction);
    params = params.set('nmconsecUen', filtro.nmconsecUen.toString());

    return firstValueFrom(
      this.http.get<EmpleadoSearchResult>(`${this.baseUrl}/buscar-uen`, { params })
    );
  }

  async obtenerDetalle(id: number): Promise<EmpleadoDetalle | null> {
    try {
      return await firstValueFrom(
        this.http.get<EmpleadoDetalle>(`${this.baseUrl}/${id}`)
      );
    } catch (error: unknown) {
      if (this.esNotFound(error)) return null;
      throw error;
    }
  }

  async crear(request: EmpleadoRequest): Promise<EmpleadoAdminResponse> {
    return firstValueFrom(
      this.http.post<EmpleadoAdminResponse>(this.baseUrl, request)
    );
  }

  async actualizar(id: number, request: EmpleadoRequest): Promise<EmpleadoAdminResponse> {
    return firstValueFrom(
      this.http.put<EmpleadoAdminResponse>(`${this.baseUrl}/${id}`, request)
    );
  }

  async obtenerFoto(id: number): Promise<Blob | null> {
    try {
      return await firstValueFrom(
        this.http.get(`${this.baseUrl}/${id}/foto`, { responseType: 'blob' })
      );
    } catch (error: unknown) {
      if (this.esNotFound(error)) return null;
      throw error;
    }
  }

  private buildFiltroParams(filtro: EmpleadoFiltro, size: number, cursor?: string, direction?: string): HttpParams {
    let params = new HttpParams().set('size', size.toString());

    if (cursor?.trim()) params = params.set('cursor', cursor.trim());
    if (direction?.trim()) params = params.set('direction', direction.trim().toUpperCase());
    if (filtro.tipoId) params = params.set('tipoId', filtro.tipoId);
    if (filtro.numId) params = params.set('numId', filtro.numId);
    if (filtro.nombre1) params = params.set('nombre1', filtro.nombre1);
    if (filtro.nombre2) params = params.set('nombre2', filtro.nombre2);
    if (filtro.apellido1) params = params.set('apellido1', filtro.apellido1);
    if (filtro.apellido2) params = params.set('apellido2', filtro.apellido2);
    if (filtro.nombreComercial) params = params.set('nombreComercial', filtro.nombreComercial);
    if (filtro.activo) params = params.set('activo', filtro.activo);
    if (filtro.cdtipoEmpleado) params = params.set('cdtipoEmpleado', filtro.cdtipoEmpleado);
    if (filtro.nmconsecOficina) params = params.set('nmconsecOficina', filtro.nmconsecOficina.toString());

    return params;
  }

  private esNotFound(error: unknown): boolean {
    return typeof error === 'object' && error !== null && 'status' in error
      && (error as { status: unknown }).status === 404;
  }

  private validarPaginacion(size: number): void {
    if (!Number.isInteger(size) || size < HttpEmpleadoRepository.PAGE_SIZE_MIN || size > HttpEmpleadoRepository.PAGE_SIZE_MAX) {
      throw new Error(
        `size debe estar entre ${HttpEmpleadoRepository.PAGE_SIZE_MIN} y ${HttpEmpleadoRepository.PAGE_SIZE_MAX}.`
      );
    }
  }
}
